import React, { Component } from "react";
import { connect } from "react-redux";
import { components } from "react-select";

import _ from "i18n";
import { ucFirst, onSuccess, onError } from "utils";
import Viewer from "../../ui/TTPModal";
import TTPLoader from "../../ui/TTPLoader";
import TTPSelect from "../../common/TTPSelect";
import Application from "../../common/Application";
import { SUPPORTED_APPS } from "../../../services/apps";
import {
  fetchGenericEmailTypes,
  createGenericEmailType,
  addTypesToCurrentSetting,
  updateTypeInCurrentSetting,
} from "thunks";

const MODE_CATALOG = "CATALOG";
const MODE_CREATE = "CREATE";

const CODE_PATTERN = /^[A-Z0-9_]+$/;

// Custom react-select option: primary label + a muted meta line (code · rank)
// so the two are visually distinct instead of near-identical.
const TypeOption = (props) => (
  <components.Option {...props}>
    <div className="add-type-option">
      <span className="add-type-option__label">{props.data.label}</span>
      <span className="add-type-option__meta">
        <span className="add-type-option__code">{props.data.code}</span>
        <span className="add-type-option__rank">
          {_("rank")} {props.data.rank}
        </span>
      </span>
    </div>
  </components.Option>
);

class AddTypeModal extends Component {
  constructor(props) {
    super(props);

    const { editType } = props;
    this.state = {
      mode: MODE_CREATE,
      selectedCatalogIds: [],
      code: editType ? `${editType.id || ""}` : "",
      labels: {
        fr: editType ? editType.labelFr || "" : "",
        nl: editType ? editType.labelNl || "" : "",
        en: editType ? editType.labelEn || "" : "",
      },
      processing: false,
    };
  }

  componentDidMount() {
    // Edit mode works purely off the record's snapshot — no catalog needed.
    if (this.isEdit()) {
      return;
    }
    const { targetApp, fetchTypes } = this.props;
    fetchTypes(targetApp);
    this.setState({ mode: MODE_CATALOG });
    this.resetCreateForm();
  }

  componentDidUpdate(prevProps) {
    // Once the catalog has (re)loaded, refresh the create-form suggestions.
    if (!this.isEdit() && prevProps.catalog !== this.props.catalog) {
      this.resetCreateForm();
    }
  }

  isEdit = () => !!this.props.editType;

  // Identifiers already present in this record — from the config `types` and
  // from the emails actually configured (their targetType). Upper-cased so the
  // comparison is robust to id/code field naming and casing differences.
  usedTypeIds = () => {
    const { currentTypes, emails } = this.props;
    const ids = new Set();
    (currentTypes || []).forEach((type) => {
      [type.id, type.code].forEach((v) => v && ids.add(`${v}`.toUpperCase()));
    });
    (emails || []).forEach((email) => {
      if (email.targetType) ids.add(`${email.targetType}`.toUpperCase());
    });
    return ids;
  };

  // Catalog types scoped to the current app that aren't already in this record.
  availableCatalogTypes = () => {
    const { catalog, targetApp } = this.props;
    const used = this.usedTypeIds();
    return (catalog || []).filter(
      (type) =>
        type.targetApp === targetApp &&
        !used.has(`${type.id}`.toUpperCase()) &&
        !(type.code && used.has(`${type.code}`.toUpperCase())),
    );
  };

  resetCreateForm = () => {
    this.setState({
      code: "",
      labels: { fr: "", nl: "", en: "" },
    });
  };

  fullCode = () => this.state.code;

  codeError = () => {
    const { code } = this.state;
    const { catalog, currentTypes } = this.props;
    // Code is read-only while editing — it already exists by design.
    if (this.isEdit()) {
      return null;
    }
    if (!code) {
      return _("code_required");
    }
    if (!CODE_PATTERN.test(code)) {
      return _("code_invalid_pattern");
    }
    const exists = (catalog || []).some(
      (type) => (type.id || "").toUpperCase() === code,
    );
    const inRecord = (currentTypes || []).some(
      (type) => (type.id || "").toUpperCase() === code,
    );
    if (exists || inRecord) {
      return _("code_already_exists");
    }
    return null;
  };

  // FR / NL / EN labels are all required so the catalog stays complete.
  requiredLanguages = () => ["fr", "nl", "en"];

  labelsValid = () =>
    this.requiredLanguages().every((lng) => !!this.state.labels[lng]?.trim());

  createFormValid = () => !this.codeError() && this.labelsValid();

  handleModeChange = (mode) => this.setState({ mode });

  handleCodeChange = (e) => {
    // Codes are free-form uppercase names (e.g. BDC_RAPPEL1, AUTH_CONFIRM_EMAIL).
    const code = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "");
    this.setState({ code });
  };

  handleLabelChange = (lng, value) => {
    this.setState((state) => ({ labels: { ...state.labels, [lng]: value } }));
  };

  handleCatalogChange = (selectedCatalogIds) => {
    this.setState({ selectedCatalogIds: selectedCatalogIds || [] });
  };

  // Every added / modified type is pinned to rank 0 so it lands on top; the
  // thunk shifts the other existing types by +1 to keep ranks distinct.
  buildSnapshot = ({ id, targetApp, labelFr, labelNl, labelEn }) => ({
    id,
    targetApp,
    labelFr: labelFr || "",
    labelNl: labelNl || "",
    labelEn: labelEn || "",
    rank: 0,
  });

  handleConfirmCatalog = () => {
    const { selectedCatalogIds } = this.state;
    const selected = this.availableCatalogTypes().filter((type) =>
      selectedCatalogIds.includes(`${type.id}`),
    );
    if (!selected.length) {
      return;
    }
    this.persistTypes(selected.map(this.buildSnapshot));
  };

  handleConfirmEdit = () => {
    if (!this.createFormValid()) {
      return;
    }
    const { targetApp, updateType, editType } = this.props;
    const { labels } = this.state;
    const snapshot = this.buildSnapshot({
      id: editType.id,
      targetApp: editType.targetApp || targetApp,
      labelFr: labels.fr,
      labelNl: labels.nl,
      labelEn: labels.en,
    });

    this.setState({ processing: true });
    this.finalize(updateType(snapshot));
  };

  handleConfirmCreate = () => {
    if (!this.createFormValid()) {
      return;
    }
    const { targetApp, createType } = this.props;
    const { labels } = this.state;
    const code = this.fullCode();
    const payload = {
      code,
      targetApp,
      labelFr: labels.fr,
      labelNl: labels.nl,
      labelEn: labels.en,
      rank: 0,
    };

    this.setState({ processing: true });
    createType(payload).then(
      (res) => {
        const created = res?.value?.data?.data || {};
        const snapshot = this.buildSnapshot({
          id: created.id || code,
          targetApp,
          labelFr: labels.fr,
          labelNl: labels.nl,
          labelEn: labels.en,
        });
        this.persistTypes([snapshot]);
      },
      (err) => {
        this.setState({ processing: false });
        onError(err);
      },
    );
  };

  persistTypes = (snapshots) => {
    const { addTypes } = this.props;
    this.setState({ processing: true });
    this.finalize(addTypes(snapshots));
  };

  // Shared tail for add / create / edit: on success refresh the list and close;
  // on error clear the loader and surface a toast.
  finalize = (promise) => {
    const { onClose, onAdded } = this.props;
    promise.then(
      (res) => {
        this.setState({ processing: false });
        onSuccess(res);
        if (onAdded) {
          onAdded();
        }
        onClose();
      },
      (err) => {
        this.setState({ processing: false });
        onError(err);
      },
    );
  };

  renderTabs() {
    const { mode } = this.state;
    return (
      <div className="add-type-modal__tabs">
        <button
          className={`add-type-modal__tab ${mode === MODE_CATALOG ? "is-active" : ""}`}
          onClick={() => this.handleModeChange(MODE_CATALOG)}
        >
          {_("add_type_from_catalog")}
        </button>
        <button
          className={`add-type-modal__tab ${mode === MODE_CREATE ? "is-active" : ""}`}
          onClick={() => this.handleModeChange(MODE_CREATE)}
        >
          {_("add_type_create_new")}
        </button>
      </div>
    );
  }

  renderCatalogMode() {
    const { userLanguage, catalogFetching } = this.props;
    const { selectedCatalogIds, processing } = this.state;
    const lng = ucFirst(userLanguage);

    const options = this.availableCatalogTypes().map((type) => ({
      id: `${type.id}`,
      label: type[`label${lng}`] || type.id,
      code: type.id,
      rank: type.rank,
    }));

    return (
      <div className="add-type-modal__body">
        <label className="add-type-modal__label">
          {_("select_types_to_add")}
        </label>
        <TTPSelect
          values={selectedCatalogIds}
          options={options}
          placeholder={catalogFetching ? _("Loading...") : _("search_a_type")}
          onChange={this.handleCatalogChange}
          isDisabled={catalogFetching}
          customOption={TypeOption}
          cssClass="add-type-select"
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          menuPosition="fixed"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
        />
        {!catalogFetching && !options.length && (
          <p className="add-type-modal__empty">
            {_("no_types_available_to_add")}
          </p>
        )}

        <div className="add-type-modal__actions">
          <button className="cancel" onClick={this.props.onClose}>
            {ucFirst(_("cancel"))}
          </button>
          <button
            className={`success ${!selectedCatalogIds.length || processing ? "disabled" : ""}`}
            onClick={this.handleConfirmCatalog}
            disabled={!selectedCatalogIds.length || processing}
          >
            {processing ? _("Processing...") : ucFirst(_("add"))}
          </button>
        </div>
      </div>
    );
  }

  renderCreateMode() {
    const { targetApp } = this.props;
    const { code, labels, processing } = this.state;
    const codeError = this.codeError();
    const languages = this.requiredLanguages();
    const isEdit = this.isEdit();
    const app = SUPPORTED_APPS.find((a) => a.code === targetApp);

    return (
      <div className="add-type-modal__body">
        <div className="add-type-modal__field">
          <label className="add-type-modal__label">{_("code")}</label>
          <input
            type="text"
            className="add-type-modal__code-input"
            value={code}
            onChange={this.handleCodeChange}
            placeholder={_("code_placeholder")}
            disabled={isEdit}
            readOnly={isEdit}
          />
          {codeError && code !== "" && (
            <span className="add-type-modal__error">{codeError}</span>
          )}
        </div>

        <div className="add-type-modal__field">
          <label className="add-type-modal__label">
            {_("Target Application")}
          </label>
          <div className="add-type-modal__app">
            <Application
              name={app ? app.name : targetApp}
              logo={app ? app.logo : undefined}
            />
          </div>
        </div>

        <div className="add-type-modal__field">
          <label className="add-type-modal__label">{_("labels")}</label>
          {languages.map((lng) => {
            return (
              <div key={lng} className="add-type-modal__label-row">
                <span className="add-type-modal__lng">
                  {lng.toUpperCase()}
                  <em className="add-type-modal__req">*</em>
                </span>
                <input
                  type="text"
                  value={labels[lng]}
                  onChange={(e) => this.handleLabelChange(lng, e.target.value)}
                  placeholder={_("label_required")}
                />
              </div>
            );
          })}
        </div>

        <div className="add-type-modal__actions">
          <button className="cancel" onClick={this.props.onClose}>
            {ucFirst(_("cancel"))}
          </button>
          <button
            className={`success ${!this.createFormValid() || processing ? "disabled" : ""}`}
            onClick={isEdit ? this.handleConfirmEdit : this.handleConfirmCreate}
            disabled={!this.createFormValid() || processing}
          >
            {processing
              ? _("Processing...")
              : ucFirst(_(isEdit ? "save_changes" : "create_and_add"))}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { onClose } = this.props;
    const { mode, processing } = this.state;
    const isEdit = this.isEdit();

    const modalStyle = {
      content: {
        width: "90%",
        maxWidth: "540px",
        height: "auto",
        maxHeight: "90%",
      },
    };

    return (
      <Viewer
        isOpen={true}
        onClose={processing ? () => {} : onClose}
        title={_(isEdit ? "edit_a_type" : "add_a_type")}
        modalStyle={modalStyle}
        shouldNotCloseOnOverlayClick={true}
        scroll={true}
      >
        <div className="add-type-modal">
          {processing && (
            <div className="add-type-modal__loader">
              <TTPLoader cssClass="add-type-modal__loader-ripple" />
            </div>
          )}
          {!isEdit && this.renderTabs()}
          {!isEdit && mode === MODE_CATALOG
            ? this.renderCatalogMode()
            : this.renderCreateMode()}
        </div>
      </Viewer>
    );
  }
}

const mapStateToProps = (state) => ({
  targetApp: state.genericEmail.currentSetting.targetApp,
  languages: state.genericEmail.currentSetting.languages,
  currentTypes: state.genericEmail.currentSetting.types || [],
  emails: state.genericEmail.currentSetting.emails.items || [],
  settingsId: state.genericEmail.currentSetting.id,
  catalog: state.genericEmail.types.items,
  catalogFetching: state.genericEmail.types.fetching,
  userLanguage: state.params.lng,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTypes: (targetApp) => dispatch(fetchGenericEmailTypes(targetApp)),
  createType: (data) => dispatch(createGenericEmailType(data)),
  addTypes: (types) => dispatch(addTypesToCurrentSetting(types)),
  updateType: (type) => dispatch(updateTypeInCurrentSetting(type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddTypeModal);
